// Main Dashboard page as specified in appmark.txt
import React, { useState, useCallback, useEffect } from "react";
import {
  ActivityIcon,
  BrainCircuit,
  ClockIcon,
  ServerIcon,
  AlertTriangleIcon,
  PlusIcon,
} from "lucide-react";
import { useDashboardData } from "../hooks/useDashboardData";
import { useWebSocket, useWebSocketMessages } from "../hooks/useWebSocket";
import { MetricCard } from "../components/Common/MetricCard";
import { WorkflowRow } from "../components/Dashboard/WorkflowRow";
import { StatusBadge } from "../components/Common/StatusBadge";
import { CreateWorkflowModal } from "../components/Workflow";
import api from "../services/api";

export const Dashboard: React.FC = () => {
  const [workflowFilter, setWorkflowFilter] = useState<string>(() => {
    // Load filter from localStorage, default to "all" (RBAC handles visibility)
    const saved = localStorage.getItem("dashboardWorkflowFilter");
    return saved || "all";
  });
  const { overview, workflows, agents, approvals, loading, error, refresh } =
    useDashboardData(workflowFilter);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState<any>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [llmProviderCount, setLlmProviderCount] = useState<number>(0);
  const [userRole, setUserRole] = useState<string>("developer");

  const { isConnected } = useWebSocket();

  // Save filter preference to localStorage
  useEffect(() => {
    localStorage.setItem("dashboardWorkflowFilter", workflowFilter);
  }, [workflowFilter]);

  // Fetch user info and LLM provider count
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [providers, userInfo] = await Promise.all([
          api.getLLMProviders(),
          api.getCurrentUser(),
        ]);
        setLlmProviderCount(providers.length);
        setUserRole(userInfo.role);
      } catch (err) {
        console.error("Failed to fetch initial data:", err);
      }
    };
    fetchInitialData();
  }, []);

  const handleRefresh = useCallback(async () => {
    await refresh(); // Refresh dashboard data
    setRefreshTrigger((prev) => prev + 1); // Trigger agent executions refresh
  }, [refresh]);

  const handleEditWorkflow = useCallback((workflow: any) => {
    setEditingWorkflow(workflow);
    setIsEditModalOpen(true);
  }, []);

  const handleEditSuccess = useCallback(() => {
    setIsEditModalOpen(false);
    setEditingWorkflow(null);
    handleRefresh();
  }, [handleRefresh]);

  // Listen for workflow updates and trigger full refresh like the refresh button
  useWebSocketMessages(
    "workflow_update",
    useCallback(
      (data: any) => {
        console.log("[DEBUG] Dashboard received workflow_update:", data);
        console.log(
          "[DEBUG] Triggering full dashboard refresh due to workflow_update"
        );
        handleRefresh();
      },
      [handleRefresh]
    )
  );

  // Listen for agent execution updates and trigger full refresh like the refresh button
  useWebSocketMessages(
    "agent_execution_update",
    useCallback(
      (data: any) => {
        console.log("[DEBUG] Dashboard received agent_execution_update:", data);
        console.log(
          "[DEBUG] Triggering full dashboard refresh due to agent_execution_update"
        );
        handleRefresh();
      },
      [handleRefresh]
    )
  );

  if (loading && !overview) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <MetricCard
              key={i}
              title="Loading..."
              value="--"
              icon={ActivityIcon}
              loading={true}
            />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
        <div className="flex">
          <AlertTriangleIcon className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800 dark:text-red-400">
              Error loading dashboard data
            </h3>
            <p className="mt-1 text-sm text-red-700 dark:text-red-300">
              {error}
            </p>
            <button
              onClick={handleRefresh}
              className="mt-2 text-sm text-red-600 dark:text-red-400 hover:underline"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-overview">
      {/* Connection Status */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Dashboard
        </h1>
        <div className="flex items-center space-x-2">
          <StatusBadge
            status={isConnected ? "online" : "offline"}
            size="sm"
            data-testid="realtime-indicator"
          />
          <span
            className="text-sm text-gray-500 dark:text-gray-400"
            data-testid="websocket-status"
          >
            {isConnected ? "Connected" : "Disconnected"}
          </span>
        </div>
      </div>

      {/* System Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="System Status"
          value={overview?.system_status || "Unknown"}
          icon={ServerIcon}
          color={
            overview?.system_status === "healthy"
              ? "green"
              : overview?.system_status === "degraded"
              ? "yellow"
              : "red"
          }
        />
        <MetricCard
          title="Active Workflows"
          value={overview?.active_workflows || 0}
          icon={ActivityIcon}
          color="blue"
        />
        <MetricCard
          title="LLM Providers"
          value={llmProviderCount || 0}
          icon={BrainCircuit}
          color="yellow"
        />
        <MetricCard
          title="Queue Depth"
          value={overview?.queue_depth || 0}
          icon={ClockIcon}
          color={overview && overview.queue_depth > 10 ? "red" : "gray"}
        />
      </div>

      {/* Active Workflows */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Active Workflows
          </h2>
          <div className="flex items-center space-x-3">
            {/* Workflow Filter (Admin Only) */}
            {userRole === "administrator" && (
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 dark:text-gray-400">
                  Show:
                </label>
                <select
                  value={workflowFilter}
                  onChange={(e) => setWorkflowFilter(e.target.value)}
                  className="px-3 py-1 bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="my">My Workflows</option>
                  <option value="all">All Workflows</option>
                  <option value="a2a">A2A Workflows</option>
                </select>
              </div>
            )}
            
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Workflow
            </button>
            <button
              onClick={handleRefresh}
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              Refresh
            </button>
          </div>
        </div>

        {workflows.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
            <ActivityIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No active workflows
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              Workflows will appear here when they are running.
            </p>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-200 dark:border-gray-700">
            {/* Header Row */}
            <div className="flex items-center px-6 py-3 gap-4 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <div className="flex-shrink-0 w-2"></div>
              
              {/* Workflow - 1/3 width */}
              <div className="flex-[1_1_33.33%] min-w-0 pr-4">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  Workflow
                </span>
              </div>
              
              {/* Remaining 4 columns - 2/3 width split evenly */}
              <div className="flex-[2_2_66.67%] min-w-0 grid grid-cols-4 gap-4 items-center">
                <div>
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Priority
                  </span>
                </div>
                <div>
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Metrics
                  </span>
                </div>
                <div>
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </span>
                </div>
                <div>
                  {/* No label for Actions */}
                </div>
              </div>
            </div>
            
            {/* Workflow Rows */}
            {workflows.map((workflow) => (
              <WorkflowRow
                key={workflow.id}
                workflow={workflow}
                onRefresh={handleRefresh}
                onEditClick={handleEditWorkflow}
                refreshTrigger={refreshTrigger}
                data-testid="workflow-card"
              />
            ))}
          </div>
        )}
      </div>

      {/* Agent Status & Approval Queue */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Agent Status */}
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6"
          data-testid="agent-status"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Agent Status
          </h3>
          <div className="space-y-3">
            {agents.map((agent) => (
              <div
                key={agent.name}
                className="flex items-center justify-between"
                data-testid="agent-item"
              >
                <div className="flex items-center">
                  <StatusBadge status={agent.status} size="sm" />
                  <span className="ml-3 text-sm font-medium text-gray-900 dark:text-white">
                    {agent.name}
                  </span>
                </div>
                {agent.currentTask && (
                  <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-32">
                    {agent.currentTask}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Approval Queue */}
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6"
          data-testid="approval-queue"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Approval Queue
          </h3>
          {approvals.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              No pending approvals
            </p>
          ) : (
            <div className="space-y-3">
              {approvals.slice(0, 5).map((approval) => (
                <div
                  key={approval.id}
                  className="flex items-center justify-between"
                  data-testid="approval-item"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {approval.title}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Waiting {approval.waitingTime}m
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      className="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 rounded"
                      data-testid="approve-button"
                    >
                      Approve
                    </button>
                    <button
                      className="px-2 py-1 text-xs bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400 rounded"
                      data-testid="reject-button"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Workflow Modal */}
      <CreateWorkflowModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleRefresh}
      />

      {/* Edit Workflow Modal */}
      <CreateWorkflowModal
        isOpen={isEditModalOpen}
        workflow={editingWorkflow}
        mode="edit"
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingWorkflow(null);
        }}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};
